import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { AccountantTSView, UserRole } from '../../../types';
import { generateMonthTemplate, generateWeekdayTemplate, generateWeekTemplate } from '../../../utils/excelMultiExportTimesheet';
import { ModalComponent } from '../../../components/ModalComponent/ModalComponent';
import { DealSkeletonLoading } from '../../../components/SkeletonLoader/DealSkeletonLoading';
import { ExportTimesheetsForm } from '../../AccountantTimesheetStatuses/components/ExportMultiTimesheetModal/ExportTimesheetsForm';
import { useMonthlyTimesheetsToExport } from '../../../queries/hooks/useMonthlyTimesheetsToExport';
import { useAllMonthlyTimesheets } from '../../../queries/hooks/useAllMonthlyTimesheets';

type ModalProps = {
  onClose: () => void;
  isOpen: boolean;
  // Role-context of the summary page opening this shared modal (Operating Partner vs Proxy); forwarded
  // to the export endpoint so a multi-role user gets the right dataset for the page they're on.
  role?: UserRole;
};

export interface IExportMultiTSForm {
  selectedPeriod: {
    year: number;
    months: {
      [month: number]: boolean;
    };
  }[];
  showDataBy: AccountantTSView | null;
}

const initialValues: IExportMultiTSForm = {
  selectedPeriod: [],
  showDataBy: null,
};

export function ExportMultiTimesheetModal({ isOpen, onClose, role }: ModalProps) {
  const [timeRangeParams, setTimeRangeParams] = useState('');
  const [searchParams] = useSearchParams();
  const [triggerExport, setTriggerExport] = useState<AccountantTSView | null>(null);
  const ids = searchParams.get('ids') ?? '';

  const {
    isLoading: isLoadingTSAvailability,
    data: availablePeriods,
    isSuccess,
  } = useAllMonthlyTimesheets(ids);

  const { data: timesheets, isLoading: isLoadingTS } = useMonthlyTimesheetsToExport({
    userIds: ids,
    timeRange: timeRangeParams,
    role,
  });

  const handleExportClick = useCallback(
    async (showDataBy: AccountantTSView) => {
      if (timesheets) {
        if (showDataBy === AccountantTSView.month) {
          await generateMonthTemplate(timesheets, true);
        }
        if (showDataBy === AccountantTSView.week) {
          await generateWeekTemplate(timesheets, true);
        }
        if (showDataBy === AccountantTSView.weekday) {
          await generateWeekdayTemplate(timesheets, true);
        }
        onClose();
      }
    },
    [onClose, timesheets]
  );

  useEffect(() => {
    if (timesheets && triggerExport) {
      handleExportClick(triggerExport);
      setTriggerExport(null);
    }
  }, [timesheets, triggerExport, handleExportClick]);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={async (values: IExportMultiTSForm) => {
        const queryParams = values.selectedPeriod
          .flatMap(({ year, months }) =>
            Object.entries(months)
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .filter(([_, value]) => value)
              .map(([month]) => `${month}-${year}`)
          )
          .join(',');
        setTimeRangeParams(queryParams);
        setTriggerExport(values.showDataBy);
      }}
    >
      {({ handleSubmit, values }) => {
        const isFormDisabled =
          values.selectedPeriod.every(({ months }) => Object.values(months).every((val) => !val)) ||
          !values.showDataBy;
        return (
          <Form>
            <ModalComponent
              isOpen={isOpen}
              onClose={onClose}
              actionButtonsProps={{
                isSubmitDisabled: isFormDisabled,
                onSubmit: handleSubmit,
                onCancel: onClose,
                submitText: isLoadingTS ? 'Exporting...' : 'Export',
              }}
              title={`Export Timesheet`}
              top='80px'
            >
              <>
                {isLoadingTSAvailability && <DealSkeletonLoading />}
                {isSuccess && <ExportTimesheetsForm availablePeriods={availablePeriods} />}
              </>
            </ModalComponent>
          </Form>
        );
      }}
    </Formik>
  );
}
